import type { ComponentPropsWithRef, ReactNode } from 'react';

import { IconCheck, IconMinus } from '@tabler/icons-react';

import { cn } from '../../../lib/cn';
import { ChoiceField } from './ChoiceField';
import { useFieldIds } from './useFieldIds';

export interface CheckboxProps
  extends Omit<ComponentPropsWithRef<'input'>, 'type' | 'checked' | 'onChange' | 'children'> {
  /** Controlled state. Omit for an uncontrolled box — `defaultChecked` still passes through. */
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /**
   * Tri-state. Paints the dash instead of the tick and announces the mixed
   * state through the input's own `indeterminate` IDL property — the only
   * mechanism a native checkbox has for it: "Authors MUST NOT use the
   * `aria-checked` attribute on `input type=checkbox` elements" (W3C html-aria),
   * and the same spec names the property ("Use the `indeterminate` IDL attribute
   * to indicate the "mixed" state"). The dash itself is driven by the
   * `data-indeterminate` attribute the input carries, so the visible mark reads
   * the same state a screen reader is told.
   */
  indeterminate?: boolean;
  label: ReactNode;
  error?: string | null;
  success?: string | null;
  hint?: string | null;
  required?: boolean;
  disabled?: boolean;
  id?: string;
}

/**
 * The painted box. Both the fill and the outline are driven off the input's own
 * DOM state (`:checked` / `[data-indeterminate]`), never off a class string, so
 * an uncontrolled box, a form reset or a native label click all keep the mark
 * and the control in agreement.
 */
const MARK_TONES: Record<'default' | 'error', string> = {
  default: cn(
    'border-line-strong bg-surface',
    'peer-checked:border-primary peer-checked:bg-primary',
    'peer-data-[indeterminate]:border-primary peer-data-[indeterminate]:bg-primary',
  ),
  error: cn(
    'border-danger bg-surface',
    'peer-checked:border-danger peer-checked:bg-danger',
    'peer-data-[indeterminate]:border-danger peer-data-[indeterminate]:bg-danger',
  ),
};

/** Both glyphs occupy the mark's single grid cell; only the live one is scaled in. */
const GLYPH =
  'col-start-1 row-start-1 scale-0 transition-transform duration-[var(--dur-fast)] ease-[var(--ease-spring)]';

/**
 * Checkbox.
 *
 * The control is a real `<input type="checkbox">` — `appearance-none` and
 * transparent, but in the DOM and in the tab order, so label association,
 * Space-to-toggle, focus and form participation are all native. The visible mark
 * is a sibling painted from the input's state, and it is the mark that carries
 * the focus ring: a ring drawn on an `opacity-0` input is a ring nobody sees.
 */
export function Checkbox({
  checked,
  onCheckedChange,
  indeterminate = false,
  label,
  error,
  success,
  hint,
  required = false,
  disabled = false,
  id: providedId,
  className,
  ref,
  ...rest
}: CheckboxProps) {
  const fieldIds = useFieldIds(providedId, { error, success, hint });

  return (
    <ChoiceField
      id={fieldIds.id}
      label={label}
      required={required}
      disabled={disabled}
      error={error}
      success={success}
      hint={hint}
      inline
      className={className}
    >
      <span className={cn('relative inline-flex shrink-0', disabled && 'opacity-45')}>
        <input
          {...rest}
          ref={(node) => {
            // `indeterminate` has no HTML attribute and React has no prop for it,
            // so it is set on the element itself; that property is the whole of
            // the mixed state for a native checkbox, both for the platform's
            // accessibility mapping and for axe. The caller's ref is forwarded
            // here because this one takes its place.
            if (node) node.indeterminate = indeterminate;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          id={fieldIds.id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onCheckedChange?.(event.currentTarget.checked)}
          required={required}
          disabled={disabled}
          aria-invalid={fieldIds.invalid || undefined}
          aria-describedby={fieldIds.describedBy}
          data-indeterminate={indeterminate || undefined}
          className={cn(
            // The input owns the hit area — `dhd-target` grows its 20px box to a
            // 44px overlay, and its own `relative` is what that overlay centres on.
            'dhd-target peer relative size-5 shrink-0 cursor-pointer appearance-none',
            'disabled:cursor-not-allowed',
          )}
        />

        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 grid place-items-center rounded-xs border-2 text-on-primary',
            'transition-[background-color,border-color,scale] duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
            'peer-hover:scale-105 peer-active:scale-95',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-focus',
            'peer-checked:[&>svg:first-child]:scale-100',
            'peer-data-[indeterminate]:[&>svg:last-child]:scale-100',
            // `checked` and `indeterminate` are both true in the select-all row
            // (`checked={all}` with `indeterminate={some && !all}`), and both
            // glyphs share one grid cell — so the tick is pushed out of the way,
            // important, because the two rules have equal specificity and only
            // source order would otherwise decide it.
            'peer-data-[indeterminate]:[&>svg:first-child]:scale-0!',
            MARK_TONES[error ? 'error' : 'default'],
          )}
        >
          <IconCheck aria-hidden="true" size={14} stroke={3} className={GLYPH} />
          <IconMinus aria-hidden="true" size={14} stroke={3} className={GLYPH} />
        </span>
      </span>
    </ChoiceField>
  );
}
